import React, { useState, useMemo, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import publicAxios from '../../utils/publicAxios';
import axiosInstance from '../../axiosInstance';
import Grid from 'apollo-react/components/Grid';
import { Formik, Form, Field } from 'formik';
import Button from 'apollo-react/components/Button';
import { renderInputComponent } from '../common/Form/renderInputComponent';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { registrationSchema } from '../../validation/auth';
import { fetchCategories } from '../Admin/ExternalUsers/externalUsersService';
import AuthLayout from './AuthLayout';
import './style.css';

const Register = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [invitationData, setInvitationData] = useState(null);

  const invitationToken = searchParams.get('invitation_token');

  useEffect(() => {
    // Use publicAxios for unauthenticated public endpoints
    publicAxios
      .get('/api/categories/public')
      .then((response) => {
        const data = response.data;
        const items = Array.isArray(data) ? data : data?.data || [];
        setCategories(items.map((cat) => ({ key: cat.id, label: cat.name })));
      })
      .catch(() => setCategories([])); // Gracefully handle errors without redirect
  }, []);

  useEffect(() => {
    if (!invitationToken) return;
    axiosInstance
      .get(`/api/endUsers/validate-invitation/${invitationToken}`)
      .then((res) => {
        setInvitationData(res.data);
        setFormData((prev) => ({
          ...prev,
          category_id: res.data.category_id,
        }));
      })
      .catch(() => {
        toast.error(t('invitation.invalidToken'), { position: 'top-center' });
      });
  }, [invitationToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const formFields = useMemo(
    () => [
      {
        name: 'first_name',
        type: 'text',
        label: t('registration.firstName'),
        required: true,
        fullWidth: false,
        tooltipText: t('registration.firstNameTooltip'),
      },
      {
        name: 'last_name',
        type: 'text',
        label: t('registration.lastName'),
        required: true,
        fullWidth: false,
        tooltipText: t('registration.lastNameTooltip'),
      },
      {
        name: 'category_id',
        type: 'dropdown',
        label: t('registration.category'),
        required: true,
        fullWidth: true,
        options: categories,
        disabled: !!invitationData,
      },
      {
        name: 'email',
        type: 'email',
        label: t('registration.emailAddress'),
        required: true,
        fullWidth: true,
        tooltipText: t('registration.emailTooltip'),
      },
      {
        name: 'password',
        type: 'password',
        label: t('registration.password'),
        required: true,
        fullWidth: true,
        tooltipText: t('registration.passwordTooltip'),
      },
      {
        name: 'confirm_password',
        type: 'password',
        label: t('registration.confirmPassword'),
        required: true,
        fullWidth: true,
        tooltipText: t('registration.confirmPasswordTooltip'),
      },
      {
        name: 'tnc',
        type: 'checkbox',
        label: t('registration.termsAndConditions'),
        required: false,
        fullWidth: true,
      },
    ],
    [t, categories, invitationData]
  );

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    category_id: '',
    email: '',
    password: '',
    confirm_password: '',
    tnc: false,
  });
  const [resp, setResp] = useState('');
  const [token, setToken] = useState('');

  const validationSchema = useMemo(
    () => toFormikValidationSchema(registrationSchema(t)),
    [t]
  );

  // Handle input changes
  const handleChange = (name, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      // Exclude tnc field from API payload - it's only for frontend validation
      const { tnc, ...apiPayload } = formData;

      axiosInstance
        .post('/api/endUsers/', apiPayload)
        .then((resp) => {
          toast.success(t('Registered Successfully!'), {
            position: 'top-center',
          });

          // Navigate to OTP verification if user is not verified
          if (!resp.data.isVerified) {
            navigate('/verify-otp');
          }
        })
        .catch((error) => {
          if (error.response && error.response.status === 400) {
            // Handle the specific error for duplicate email
            toast.error(error.response.data.message, {
              position: 'top-center',
            });
          } else {
            setResp(t('Error creating form'), ':', error);
          }
        });
    } catch (err) {
      setResp(t('Error submitting form'), ':', err);
      console.error('Error submitting form: ', err);
    }
  };

  return (
    <AuthLayout>
      <Grid
        item
        justifyContent="center"
        width="463px"
        position="relative"
        zIndex={1}
      >
        <Grid container justifyContent="left" className="signInTitleGrid">
          <div>
            <h2>Register my account</h2>
            <p>We&apos;re glad you&apos;re here.</p>
          </div>
        </Grid>
        <Grid container className="formContainer">
          <Grid container justifyContent="center" sx={{ width: '100%' }}>
            <Grid size={12}>
              <Formik
                initialValues={formData}
                enableReinitialize={true}
                validationSchema={validationSchema}
                onSubmit={() => {
                  handleSubmit();
                }}
              >
                {({ handleSubmit, setFieldValue }) => (
                  <Form onSubmit={handleSubmit}>
                    <Grid container spacing={2} sx={{ width: '100%' }}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Field name="first_name">
                          {({ field: formikField, meta }) => {
                            const handleFieldChange = (value) => {
                              setFieldValue(formikField.name, value);
                              handleChange(formikField.name, value);
                            };

                            return renderInputComponent(
                              {
                                name: 'first_name',
                                type: 'text',
                                label: 'First Name',
                                required: true,
                                tooltipText: t('registration.firstNameTooltip'),
                              },
                              formData,
                              formikField,
                              meta,
                              handleFieldChange
                            );
                          }}
                        </Field>
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Field name="last_name">
                          {({ field: formikField, meta }) => {
                            const handleFieldChange = (value) => {
                              setFieldValue(formikField.name, value);
                              handleChange(formikField.name, value);
                            };

                            return renderInputComponent(
                              {
                                name: 'last_name',
                                type: 'text',
                                label: 'Last Name',
                                required: true,
                                tooltipText: t('registration.lastNameTooltip'),
                              },
                              formData,
                              formikField,
                              meta,
                              handleFieldChange
                            );
                          }}
                        </Field>
                      </Grid>
                    </Grid>

                    {formFields
                      .filter(
                        (field) =>
                          field.name !== 'first_name' &&
                          field.name !== 'last_name'
                      )
                      .map((field) => (
                        <Field key={field.name} name={field.name}>
                          {({ field: formikField, meta }) => {
                            const handleFieldChange = (value) => {
                              setFieldValue(formikField.name, value);
                              handleChange(formikField.name, value);
                            };

                            return renderInputComponent(
                              field,
                              formData,
                              formikField,
                              meta,
                              handleFieldChange
                            );
                          }}
                        </Field>
                      ))}

                    <Button
                      variant="primary"
                      type="submit"
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: 'none',
                        margin: '20px 0',
                        cursor: 'pointer',
                      }}
                    >
                      Register
                    </Button>
                  </Form>
                )}
              </Formik>
            </Grid>
          </Grid>
        </Grid>
        <Grid container justifyContent="center">
          <p className="signUpText">
            Already have an account? {''}
            <a href="/login" className="signUpLink">
              Sign In
            </a>
          </p>
        </Grid>
      </Grid>
    </AuthLayout>
  );
};

export default Register;
